import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock, Camera, Pill, CreditCard, FlaskConical, BadgeCheck, Receipt, Upload, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import DashboardLayout from '../components/DashboardLayout';
import GlobalUploadModal from '../components/GlobalUploadModal';
import { HUB_KINDS, KIND_BY_KEY } from '../components/uploadhub/hubKinds';
import { HubReview } from '../components/uploadhub/HubReview';

const ICONS = { FileText, Clock, Camera, Pill, CreditCard, FlaskConical, BadgeCheck, Receipt };
const TONE = { Reading: 'bg-blue-100 text-blue-800', 'Needs review': 'bg-amber-100 text-amber-800', Applying: 'bg-blue-100 text-blue-800', Applied: 'bg-green-100 text-green-800' };
const errText = (e) => { const d = e.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg).join(', ') : typeof d === 'string' ? d : ''; };

export default function UploadHubPage() {
  const navigate = useNavigate();
  const [kind, setKind] = useState(null);
  const [isOffice, setIsOffice] = useState(false);
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [patientId, setPatientId] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [modal, setModal] = useState(false);
  const input = useRef(null);
  const load = useCallback(async () => {
    try { const r = await api.get('/upload-hub'); setItems(r.data.items); setIsOffice(r.data.isOffice); } catch { toast.error('Could not load uploads'); }
  }, []);
  useEffect(() => {
    load();
    api.get('/patients').then((r) => setPatients((r.data.patients || []).filter((p) => p.status !== 'Discharged')
      .map((p) => ({ id: p.id, name: `${p.firstName || ''} ${p.lastName || ''}`.trim() })).sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    api.get('/timeclock/staff').then((r) => setStaff(r.data.staff || [])).catch(() => {});
  }, [load]);
  useEffect(() => {
    if (!items.some((i) => i.status === 'Reading')) return undefined;
    const t = setTimeout(load, 4000); return () => clearTimeout(t);
  }, [items, load]);
  const pick = (k) => {
    if (k.modal) { setModal(true); return; }
    if (k.link) { navigate(k.link); return; }
    setKind(k.key);
  };
  const upload = async (files) => {
    const k = KIND_BY_KEY[kind];
    if (k.patient && !patientId) { toast.error('Choose the patient first'); return; }
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append('file', f); fd.append('kind', kind); if (k.patient) fd.append('patientId', patientId);
      try { await api.post('/upload-hub', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(`${f.name} uploaded - reading it now`); }
      catch (e) { toast.error(errText(e) || `Could not upload ${f.name}`); }
    }
    setBusy(false); if (input.current) input.current.value = ''; load();
  };
  const remove = async (i) => {
    try { await api.delete(`/upload-hub/${i.id}`); load(); } catch (e) { toast.error(errText(e) || 'Could not delete'); }
  };
  const k = kind && KIND_BY_KEY[kind];
  const tiles = HUB_KINDS.filter((t) => !t.office || isOffice);
  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-[1500px]" data-testid="upload-hub-page">
        <div><p className="text-xs font-bold uppercase tracking-wide text-[#b8860b]">Upload &amp; Auto-Populate</p><h1 className="text-2xl font-bold text-gray-900">Upload Hub</h1>
          <p className="text-sm text-gray-600">Pick what you are uploading, add a PDF or a phone photo, check what was read, then fill it into the app.</p></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hub-tiles">
          {tiles.map((t) => { const I = ICONS[t.icon]; return (
            <button key={t.key} onClick={() => pick(t)} data-testid={`hub-tile-${t.key}`}
              className={`text-left rounded-xl border p-4 transition-colors ${kind === t.key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:border-[#d4af37]'}`}>
              <I className={`w-6 h-6 mb-2 ${kind === t.key ? 'text-[#d4af37]' : 'text-[#b8860b]'}`} />
              <p className="font-semibold">{t.title}</p><p className={`text-xs mt-1 ${kind === t.key ? 'text-gray-300' : 'text-gray-500'}`}>{t.desc}</p>
            </button>); })}
        </div>
        {k && (
          <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload([...e.dataTransfer.files]); }}
            className="border-2 border-dashed border-[#d4af37] rounded-xl p-5 bg-amber-50/40 flex flex-wrap items-center gap-4" data-testid="hub-dropzone">
            <Upload className="w-8 h-8 text-[#b8860b]" />
            <div className="flex-1 min-w-[220px]"><p className="font-semibold">{k.title}: drop files here or choose them</p><p className="text-sm text-gray-600">PDF or photo (JPG, PNG, HEIC), up to 15 MB. On a phone you can take the picture directly.</p></div>
            {k.patient && (
              <select value={patientId} onChange={(e) => setPatientId(e.target.value)} className="border rounded px-2 py-2 text-sm max-w-xs" data-testid="hub-patient-select">
                <option value="">Choose patient</option>{patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
            )}
            <input ref={input} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="hub-file-input" />
            <button disabled={busy} onClick={() => input.current?.click()} className="px-4 py-2 rounded-lg bg-gray-900 text-[#d4af37] font-semibold disabled:opacity-50" data-testid="hub-upload-btn">{busy ? 'Uploading…' : 'Choose file / take photo'}</button>
          </div>
        )}
        <div className="border rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-900 text-[#d4af37] text-left"><tr>{['Uploaded', 'Type', 'Patient', 'File', 'Status', 'Result', ''].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t" data-testid={`hub-row-${i.id}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">{new Date(i.uploadedAt).toLocaleString()}<div className="text-xs text-gray-500">{i.uploadedBy}</div></td>
                  <td className="px-2 py-1.5">{KIND_BY_KEY[i.kind]?.title}</td>
                  <td className="px-2 py-1.5">{i.patientName || '—'}</td>
                  <td className="px-2 py-1.5 max-w-[200px] truncate">{i.fileName}</td>
                  <td className="px-2 py-1.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${TONE[i.status] || ''}`} data-testid={`hub-status-${i.id}`}>{i.status}</span></td>
                  <td className="px-2 py-1.5 text-xs text-gray-600 max-w-[320px]">{i.result || i.readError}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <button disabled={i.status === 'Reading'} onClick={() => setOpen(i.id)} className="px-2 py-1 rounded border text-xs disabled:opacity-40" data-testid={`hub-open-${i.id}`}>
                      {i.status === 'Reading' ? 'Reading…' : i.status === 'Applied' ? <Eye className="w-4 h-4" /> : 'Review'}</button>
                    {i.status !== 'Applied' && <button onClick={() => remove(i)} className="ml-1 p-1 text-red-600" aria-label="Delete" data-testid={`hub-delete-${i.id}`}><Trash2 className="w-4 h-4" /></button>}
                  </td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={7} className="px-2 py-4 text-gray-500" data-testid="hub-empty">Nothing uploaded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {open && <HubReview id={open} staff={staff} onClose={() => setOpen(null)} onDone={load} />}
      <GlobalUploadModal isOpen={modal} onClose={() => setModal(false)} />
    </DashboardLayout>
  );
}
